import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  COLOR_DENIM,
  COLOR_DISABLED_BG,
  COLOR_DISABLED_TEXT,
  COLOR_HUMMINGBIRD,
  COLOR_WHITE,
} from '../../popTheme';
import { clearPasswordChangeStatus } from '../../redux/reducers/authorization';
import { sendChangePasswordSaga } from '../../redux/sagas/sendChangePasswordSaga';
import { PopInputField } from '../form/PopInputField';
import { SettingsFormLabel } from './SettingsFormLabel';
import { selectProfile } from 'redux/reducers/session';

export const VALIDATION_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*_+=`\-|(){}[\]:;"'<>,.?/])[A-Za-z\d~!@#$%^&*_+=`\-|(){}[\]:;"'<>,.?/]{8,}$/;

export const ChangePassword = () => {
  const [formInput, setFormInput] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [formError, setFormError] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const clearFormField = (name) => {
    setFormInput(() => ({ ...formInput, [name]: '' }));
    setFormError(() => ({ ...formError, [name]: '' }));
  }

  const handleSuccessfulChangePassword = () => {
    // Clear the form.
    setFormInput({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
  };

  const isChangePasswordSuccess = useSelector(
    (state) => state?.authorization?.changePasswordResult?.success,
  );
  const changePasswordErrorMessage = useSelector(
    (state) => state?.authorization?.changePasswordResult?.errorMessage,
  );
  const userEmail = useSelector(selectProfile)?.email;

  useEffect(() => {
    if (isChangePasswordSuccess) {
      handleSuccessfulChangePassword();
    }
  });

  const theme = useTheme();
  const dispatch = useDispatch();

  const intl = useIntl();
  const messages = defineMessages({
    changePasswordTitle: {
      id: 'changePassword.title',
      defaultMessage: 'Change Password',
      description: 'Settings page change password title text',
    },
    changePasswordButtonText: {
      id: 'changePassword.button.text',
      defaultMessage: 'Change Password',
      description: 'Settings page change password button text',
    },
    changePasswordCurrentPassword: {
      id: 'changePassword.current_password',
      defaultMessage: 'Current Password',
      description: 'Settings page change password Current Password field title',
    },
    changePasswordNewPassword: {
      id: 'changePassword.new_password',
      defaultMessage: 'New Password',
      description: 'Settings page change password New Password field title',
    },
    changePasswordConfirmNewPassword: {
      id: 'changePassword.confirm_new_password',
      defaultMessage: 'Confirm New Password',
      description: 'Settings page change password Confirm New Password field title',
    },
    changePasswordRules: {
      id: 'changePassword.rules',
      defaultMessage:
        'Password must be at least 8 characters, including 1 capitalized letter, 1 small letter, 1 special character and 1 numeric character.',
      description: 'Settings page new password rules',
    },
    changePasswordErrorFieldRequired: {
      id: 'changePassword.error.field_required',
      defaultMessage: 'Field is required',
      description: 'Settings page change password error field required',
    },
    changePasswordErrorPasswordsDoNotMatch: {
      id: 'changePassword.error.passwords_do_not_match',
      defaultMessage: 'Passwords do not match.',
      description: 'Settings page change password error do not match',
    },
    changePasswordErrorPasswordsPattern: {
      id: 'changePassword.error.passwords_pattern',
      defaultMessage: 'Password does not meet requirements.',
      description: 'Settings page change password error pattern',
    },
    changePasswordSuccessMessage: {
      id: 'changePassword.success_message',
      defaultMessage: 'Successfully Changed Password',
      description: 'Settings page change password success message',
    },
  });

  const validateField = (name, value) => {
    let currentPasswordError = formError.currentPassword;
    let newPasswordError = formError.newPassword;
    let confirmPasswordError = formError.confirmNewPassword;

    switch (name) {
      case 'currentPassword':
        currentPasswordError = '';
        break;
      case 'newPassword':
        if (formInput.confirmNewPassword.length !== 0 && value !== formInput.confirmNewPassword) {
          confirmPasswordError = intl.formatMessage(
            messages.changePasswordErrorPasswordsDoNotMatch,
          );
        } else {
          confirmPasswordError = '';
        }

        if (
          value.length === 0 ||
          (VALIDATION_PASSWORD_REGEX.test(value) && value.toLowerCase() !== userEmail.toLowerCase())
        ) {
          newPasswordError = '';
        } else {
          newPasswordError = intl.formatMessage(messages.changePasswordErrorPasswordsPattern);
        }

        break;
      case 'confirmNewPassword':
        confirmPasswordError = '';
        if (value.length !== 0 && value !== formInput.newPassword) {
          confirmPasswordError = intl.formatMessage(
            messages.changePasswordErrorPasswordsDoNotMatch,
          );
        }
        break;
      default:
        break;
    }

    setFormError({
      currentPassword: currentPasswordError,
      newPassword: newPasswordError,
      confirmNewPassword: confirmPasswordError,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Clear the change password status if necessary.
    if (isChangePasswordSuccess === true || !!changePasswordErrorMessage) {
      dispatch(clearPasswordChangeStatus());
    }

    setFormInput(() => ({ ...formInput, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = (event) => {
    dispatch(sendChangePasswordSaga(formInput.currentPassword, formInput.newPassword));
    event.preventDefault();
  };

  return (
    <Container
      sx={{
        bgcolor: COLOR_DENIM,
        height: '100%',
        borderRadius: '0.75rem',
        py: '2rem',
        [theme.breakpoints.only('xs')]: {
          my: '1rem',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" color={COLOR_WHITE} mb={'0.5rem'}>
          {intl.formatMessage(messages.changePasswordTitle)}
        </Typography>

        <form onSubmit={handleSubmit}>
          <FormControl fullWidth>
            <SettingsFormLabel
              subtitle={intl.formatMessage(messages.changePasswordCurrentPassword)}
              isRequired={true}
              inputId="currentPassword"
              color={COLOR_WHITE}
            />
            <PopInputField
              fullWidth
              name="currentPassword"
              type="password"
              id="currentPassword"
              size="small"
              inputProps={{ 'data-testid': 'change-password-current-password-input' }}
              onChange={handleInputChange}
              value={formInput.currentPassword}
              error={formError.currentPassword.length > 0 || changePasswordErrorMessage?.length > 0}
              onClickClearButton={() => {clearFormField('currentPassword');dispatch(clearPasswordChangeStatus());}}
            />
            <FormHelperText error={true}>
              {formError.currentPassword || changePasswordErrorMessage}
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <SettingsFormLabel
              subtitle={intl.formatMessage(messages.changePasswordNewPassword)}
              isRequired={true}
              color={COLOR_WHITE}
              inputId="newPassword"
            />
            <PopInputField
              error={formError.newPassword?.length > 0}
              required
              fullWidth
              name="newPassword"
              type="password"
              id="newPassword"
              size="small"
              inputProps={{ 'data-testid': 'change-password-new-password-input' }}
              onChange={handleInputChange}
              value={formInput.newPassword}
              onClickClearButton={() => clearFormField('newPassword')}
            />
            <FormHelperText error>{formError.newPassword}</FormHelperText>
            <Typography
              variant="subtitle1"
              color={COLOR_WHITE}
              mt={'1rem'}
              lineHeight={'1.21874rem'}
              align={'center'}
            >
              {intl.formatMessage(messages.changePasswordRules)}
            </Typography>
          </FormControl>
          <FormControl fullWidth>
            <SettingsFormLabel
              subtitle={intl.formatMessage(messages.changePasswordConfirmNewPassword)}
              isRequired={true}
              color={COLOR_WHITE}
            />
            <PopInputField
              error={formError.confirmNewPassword?.length > 0}
              required
              fullWidth
              name="confirmNewPassword"
              type="password"
              id="confirmNewPassword"
              size="small"
              inputProps={{ 'data-testid': 'change-password-confirm-new-password-input' }}
              onChange={handleInputChange}
              value={formInput.confirmNewPassword}
              onClickClearButton={() => clearFormField('confirmNewPassword')}
            />
            <FormHelperText error={true}>{formError.confirmNewPassword}</FormHelperText>
          </FormControl>
          <FormControl fullWidth sx={{ alignItems: 'center' }} error={!isChangePasswordSuccess}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !(
                  formError.currentPassword?.length === 0 &&
                  formError.newPassword?.length === 0 &&
                  formError.confirmNewPassword?.length === 0 &&
                  formInput.currentPassword?.length > 0 &&
                  formInput.newPassword?.length > 0 &&
                  formInput.confirmNewPassword?.length > 0
                )
              }
              data-testid="change-password-button"
              sx={{
                mt: '1rem',
                mb: '1rem',
                width: '20rem',
                color: COLOR_DENIM,
                bgcolor: COLOR_HUMMINGBIRD,
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
                [theme.breakpoints.up('sm')]: {
                  maxWidth: '15rem',
                },
                [theme.breakpoints.up('xs')]: {
                  maxWidth: '10rem',
                  fontSize: '0.8rem',
                },
              }}
            >
              {intl.formatMessage(messages.changePasswordButtonText)}
            </Button>
            <FormHelperText
              sx={{ display: isChangePasswordSuccess ? 'block' : 'none', color: COLOR_WHITE }}
            >
              {intl.formatMessage(messages.changePasswordSuccessMessage)}
            </FormHelperText>
          </FormControl>
        </form>
      </Box>
    </Container>
  );
};
